import React from 'react';
import {
    Popover,
    Button,
    MenuItem,
    Select,
    TextField,
    FormControl,
    InputLabel,
    Autocomplete,
    Box,
    styled,
    useTokens,
} from '@barracuda-internal/bds-core';
import { useScanFilters, VALID_SEVERITIES } from './hooks/useScanFilters';

const STATUS_VALUES = ['completed', 'failed', 'processing', 'partial'];
const CONTENT_TYPES = ['application/json', 'text/plain', 'application/xml'];

const SEVERITY_LABEL = {
    critical: 'Critical',
    high:     'High',
    medium:   'Medium',
    low:      'Low',
    info:     'Info',
};

// styled() for 3 CSS properties — pass token values as props, never call useTokens() inside
const PopoverContent = styled(Box, {
    shouldForwardProp: (prop) => !['padding', 'gap'].includes(prop),
})(({ padding, gap }) => ({
    padding,
    display: 'grid',
    gap,
}));

export default function MoreFiltersPopover() {
    const tokens = useTokens();
    const { filters, setFilter } = useScanFilters();
    const [anchor, setAnchor] = React.useState(null);

    const handleOpen = (e) => setAnchor(e.currentTarget);
    const handleClose = () => setAnchor(null);

    // Theme-aware paper background so the popover + inner Select menus don't
    // fall back to MUI's default white paper in dark mode.
    const themedPaperSx = {
        backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
        color: tokens.aliasesColorTextStaticEmphasis,
    };
    const menuProps = { PaperProps: { sx: themedPaperSx } };
    const autocompleteSlotProps = { paper: { sx: themedPaperSx } };

    return (
        <>
            <Button bdsType="interactiveNeutral" onClick={handleOpen}>
                More filters
            </Button>

            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: themedPaperSx }}
            >
                <PopoverContent
                    padding={tokens.aliasesSpaceContainerDefault}
                    gap={tokens.aliasesSpaceComponentDefault}
                >
                    {/* Status — shrink+notched keeps label pinned to notch so it never overlaps the value */}
                    <FormControl fullWidth>
                        <InputLabel shrink id="mfp-status-label">Status</InputLabel>
                        <Select
                            labelId="mfp-status-label"
                            MenuProps={menuProps}
                            label="Status"
                            notched
                            value={filters.status || ''}
                            onChange={(e) => setFilter('status', e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value=""><em>Any status</em></MenuItem>
                            {STATUS_VALUES.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Tenant ID"
                        value={filters.tenantId}
                        onChange={(e) => setFilter('tenantId', e.target.value)}
                        fullWidth
                    />

                    <TextField
                        label="User ID"
                        value={filters.userId}
                        onChange={(e) => setFilter('userId', e.target.value)}
                        fullWidth
                    />

                    {/* Content type — shrink+notched keeps label pinned to notch */}
                    <FormControl fullWidth>
                        <InputLabel shrink id="mfp-content-type-label">Content type</InputLabel>
                        <Select
                            labelId="mfp-content-type-label"
                            MenuProps={menuProps}
                            label="Content type"
                            notched
                            value={filters.contentType || ''}
                            onChange={(e) => setFilter('contentType', e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value=""><em>Any content type</em></MenuItem>
                            {CONTENT_TYPES.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Autocomplete
                        multiple
                        size="small"
                        options={VALID_SEVERITIES}
                        getOptionLabel={(o) => SEVERITY_LABEL[o] || o}
                        value={filters.severities || []}
                        onChange={(_, v) => setFilter('severities', v)}
                        renderTags={() => null}
                        slotProps={autocompleteSlotProps}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    filters.severities?.length
                                        ? `Severity (${filters.severities.length})`
                                        : 'Severity'
                                }
                            />
                        )}
                    />

                    <TextField
                        label="Min confidence"
                        type="number"
                        inputProps={{ min: 0, max: 1, step: 0.05 }}
                        value={filters.minConfidence != null ? filters.minConfidence : ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            setFilter('minConfidence', v === '' ? null : Number(v));
                        }}
                        fullWidth
                    />
                </PopoverContent>
            </Popover>
        </>
    );
}
